import { Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import {
    CheckCircleOutline as AppliedIcon,
    ErrorOutline as FailedIcon,
    ReportProblemOutlined as RejectedIcon,
} from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BridgeActivityResponse,
    BridgeStatusMessage,
    BridgeStatusSeverity,
    latestSettledCallId,
    selectHeaderStatus,
} from '../domain/bridgeActivity';
import useQuery, { useDeferredQuery } from '../hooks/useQuery';

/**
 * Shows what the legacy Delphi bridge is doing, in the app header.
 *
 * While the transition runs, the Delphi application replays every committed
 * transaction into this app. Without an indicator that is completely invisible:
 * the operator has no way to tell a working bridge from a silent one. A call in
 * progress is announced while it runs, and its outcome stays for five seconds.
 *
 * Nothing renders at all when the bridge is not configured, so a deployment
 * outside the shadow rollout neither shows the chip nor keeps polling for it.
 *
 * Because it is already watching the log, it is also the header's cue that a
 * legacy transaction has just landed: `onCallSettled` fires once per finished
 * call, so the cash figures beside it can be re-read straight away instead of
 * waiting out their thirty-second poll.
 *
 * Reference: documentation/implementation_plan_legacy_delphi_bridge_api.md (section 7.7)
 */

/** How often the log is polled while the bridge is enabled. */
const POLL_INTERVAL_MS = 2000;

/**
 * How often the visibility rule is re-evaluated. Finer than the poll so a
 * message disappears five seconds after the call ended rather than at whatever
 * moment the next poll happens to land on.
 */
const EXPIRY_TICK_MS = 500;

const SEVERITY_COLOR: Record<BridgeStatusSeverity, 'info' | 'success' | 'warning' | 'error'> = {
    info: 'info',
    success: 'success',
    warning: 'warning',
    error: 'error',
};

/** One translation key per outcome, so no key is assembled from a variable. */
const SEVERITY_LABEL_KEY: Record<BridgeStatusSeverity, string> = {
    info: 'header.bridge.inProgress',
    success: 'header.bridge.applied',
    warning: 'header.bridge.rejected',
    error: 'header.bridge.failed',
};

const statusIcon = (message: BridgeStatusMessage) => {
    if (message.status === 'in_progress') {
        return <CircularProgress size={12} thickness={6} />;
    }
    if (message.severity === 'error') {
        return <FailedIcon sx={{ fontSize: 14 }} />;
    }
    if (message.severity === 'warning') {
        return <RejectedIcon sx={{ fontSize: 14 }} />;
    }
    return <AppliedIcon sx={{ fontSize: 14 }} />;
};

interface BridgeStatusIndicatorProps {
    /**
     * Called once for each bridge call that finishes while the header is open.
     * Also fires for the newest already-finished call seen on mount, which
     * costs one extra read and removes the ambiguity of a call that settled
     * between the first render and the first poll.
     */
    onCallSettled?: () => void;
}

const BridgeStatusIndicator = ({ onCallSettled }: BridgeStatusIndicatorProps) => {
    const { t } = useTranslation();

    // One cheap probe decides whether polling is worth starting at all.
    const { data: probe } = useQuery<BridgeActivityResponse>({
        path: '/legacy-bridge-activity',
    });
    const enabled = probe?.enabled === true;

    const { data: activity } = useDeferredQuery<BridgeActivityResponse>({
        path: '/legacy-bridge-activity',
        shouldExecute: enabled,
        pollInterval: POLL_INTERVAL_MS,
    });

    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!enabled) {
            return;
        }
        const interval = setInterval(() => setNow(Date.now()), EXPIRY_TICK_MS);
        return () => clearInterval(interval);
    }, [enabled]);

    const calls = activity?.calls ?? probe?.calls ?? [];
    const settledCallId = latestSettledCallId(calls);
    const notifiedCallId = useRef<string | null>(null);

    useEffect(() => {
        if (settledCallId === null || settledCallId === notifiedCallId.current) {
            return;
        }
        notifiedCallId.current = settledCallId;
        onCallSettled?.();
    }, [settledCallId, onCallSettled]);

    if (!enabled) {
        return null;
    }

    const message = selectHeaderStatus(calls, now);

    if (!message) {
        return null;
    }

    const operationLabel = message.operationLabel || t('header.bridge.noOperation');
    const label = t(SEVERITY_LABEL_KEY[message.severity], {
        operations: operationLabel,
        count: message.eventCount,
    });

    return (
        <Tooltip
            title={
                <Box>
                    <Typography variant="subtitle2">{t('header.bridge.title')}</Typography>
                    <Typography variant="caption" display="block">
                        {t('header.bridge.operations')}:{' '}
                        {message.operations.join(', ') || t('header.bridge.noOperation')}
                    </Typography>
                    <Typography variant="caption" display="block">
                        {t('header.bridge.eventCount')}: {message.eventCount}
                    </Typography>
                    <Typography variant="caption" display="block">
                        {t('header.bridge.cashPoint')}: {message.cashPointId ?? '-'}
                    </Typography>
                    <Typography variant="caption" display="block">
                        {t('header.bridge.workstation')}: {message.workstation ?? '-'}
                    </Typography>
                    {message.durationMs !== null && (
                        <Typography variant="caption" display="block">
                            {t('header.bridge.duration')}: {message.durationMs} ms
                        </Typography>
                    )}
                    {message.httpStatus !== null && (
                        <Typography variant="caption" display="block">
                            {t('header.bridge.httpStatus')}: {message.httpStatus}
                        </Typography>
                    )}
                    {message.error && (
                        <Typography variant="caption" display="block">
                            {t('header.bridge.error')}: {message.error}
                        </Typography>
                    )}
                </Box>
            }
        >
            <Chip
                data-testid="header-bridge-status"
                data-bridge-severity={message.severity}
                icon={statusIcon(message)}
                label={label}
                color={SEVERITY_COLOR[message.severity]}
                variant="outlined"
                size="small"
                sx={{ height: 24, fontSize: '0.7rem', maxWidth: 260 }}
            />
        </Tooltip>
    );
};

export default BridgeStatusIndicator;
